import { Endpoints } from "../../misc/Endpoints";
import ApiService from "../../services/ApiService";
import { CodReconcileRepositoryInterface } from "@/data/persistences/repositories/contracts/CodReconcileRepositoryInterface";
import { CodReconcileMapper } from "@/data/persistences/mappers/CodReconcileMapper";
import {
  CodDashboardDetailCourier,
  CourierDetail,
  CourierDetailApiRequest,
  CourierDetailList,
  CourierDetailPendingReconcile,
  CourierHistory,
  CourierNotes,
  CourierSummary,
  DashboardDeliveryCourierDetail,
  TaskSetllementCourier
} from "@/domain/entities/CodReconcile";
import {
  GetCourierNotes,
  SubmitCourierNotes,
  SubmitCourierNotesPhotos,
  TaskSettlementCourierParams
} from "@/data/payload/api/CodReconcileApiRequest";

export class CodReconcileApiRepository
  implements CodReconcileRepositoryInterface {
  private service: ApiService;
  private mapper: CodReconcileMapper;
  private endpoints: Endpoints;

  constructor(
    service: ApiService,
    mapper: CodReconcileMapper,
    endpoints: Endpoints
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  async getCourierDetailSummary(
    params: CourierDetailApiRequest
  ): Promise<CodDashboardDetailCourier> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailCourierSummary(params)
    );

    return this.mapper.convertDetailCourierPartnerCodDashboardFromApi(resp);
  }

  async getCourierSummaryDetailV2(
    params: CourierDetailApiRequest,
    version: string
  ): Promise<CourierSummary> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getCourierSummaryDetailV2(params, version)
    );

    return this.mapper.convertCourierSummaryDetailFromApiV2(resp);
  }

  async getCourierDetail(
    params: CourierDetailApiRequest,
    version: string
  ): Promise<CourierDetailList> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailCourierCodV2(params, version)
    );

    return this.mapper.convertDetailCourierCodFromApi(resp);
  }

  async getCourierDetailV2(
    params: CourierDetailApiRequest,
    version: string
  ): Promise<CourierDetail[]> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailCourierCodV2(params, version)
    );

    return this.mapper.convertDetailCourierCodFromApiV2(resp);
  }

  async getDetailCourierPendingReconcile(
    params: CourierDetailApiRequest,
    version: string
  ): Promise<CourierDetailPendingReconcile[]> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailCourierPendingReconcile(params, version)
    );

    return this.mapper.convertDetailCourierPendingReconcileFromApi(resp);
  }

  async getDashboardDeliveryCourierDetail(
    phone: string
  ): Promise<DashboardDeliveryCourierDetail> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getDetailCourierByPhone(phone)
    );

    return this.mapper.convertDashboardDeliveryCourierDetailFromApi(resp);
  }

  async getCourierNotes(payload: GetCourierNotes): Promise<CourierNotes> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getCourierNotes(payload.courierId),
      payload.params
    );

    return this.mapper.convertCourierNotesFromApi(resp);
  }

  async getCourierHistory(payload: GetCourierNotes): Promise<CourierHistory[]> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getCourierHistory(payload.courierId, payload.reconcileFor),
      payload.params
    );

    return this.mapper.convertCourierHistoryFromApi(resp);
  }

  async uploadCourierNotesPhotos(
    payload: SubmitCourierNotesPhotos
  ): Promise<boolean> {
    await this.service.invoke(
      "PATCH",
      this.endpoints.getCourierNotes(payload.courierId),
      undefined,
      payload
    );

    return true;
  }
  async submitCourierNotes(payload: SubmitCourierNotes): Promise<boolean> {
    await this.service.invoke(
      "PATCH",
      this.endpoints.getCourierNotes(payload.courierId),
      undefined,
      payload
    );

    return true;
  }

  async getTaskSettlementCourier(
    params: TaskSettlementCourierParams
  ): Promise<TaskSetllementCourier[]> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getTaskSettlementCourier(params)
    );

    return this.mapper.convertTaskSetllementCourierFromApiV2(resp);
  }

  async getTaskSettlementCourierDetail(
    id: number
  ): Promise<TaskSetllementCourier> {
    const resp = await this.service.invoke(
      "GET",
      this.endpoints.getTaskSettlementCourierDetail(id)
    );

    return this.mapper.convertTaskSetllementCourierDetailFromApi(resp);
  }
}
